import { useEffect, useState } from 'react';
import { adminEnquiries, adminNewsletter, hideEnquiries, hideNewsletter } from '@/lib/admin';

/**
 * Small shared helper that keeps the row-selection logic identical
 * between the two lists. Returns handlers + a boolean helper for the
 * "select all" checkbox.
 */
function useSelection(items) {
    const [selected, setSelected] = useState(() => new Set());
    const toggle = (id) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };
    const toggleAll = () => {
        setSelected((prev) => {
            const all = items.map((i) => i.id);
            if (prev.size === all.length && all.every((id) => prev.has(id))) return new Set();
            return new Set(all);
        });
    };
    const clear = () => setSelected(new Set());
    const allSelected = items.length > 0 && selected.size === items.length;
    return { selected, toggle, toggleAll, clear, allSelected };
}

export function AdminEnquiries() {
    const [items, setItems] = useState([]);
    const [busy, setBusy] = useState(false);
    const { selected, toggle, toggleAll, clear, allSelected } = useSelection(items);
    const reload = () => adminEnquiries().then(setItems);
    useEffect(() => { reload(); }, []);

    const removeIds = async (ids, msg) => {
        if (!ids.length) return;
        if (!window.confirm(msg)) return;
        setBusy(true);
        try {
            await hideEnquiries(ids);
            await reload();
            clear();
        } finally { setBusy(false); }
    };

    return (
        <div data-testid="admin-enquiries" className="py-10">
            <span className="section-no">§ Inbox</span>
            <h1 className="serif text-5xl md:text-7xl mt-3 leading-[0.95]">Enquiries</h1>
            <p className="literary mt-4">{items.length} total</p>

            {items.length > 0 && (
                <div className="mt-8 flex items-center gap-6 rule-bottom pb-4">
                    <label className="flex items-center gap-2 caption cursor-pointer" data-testid="enquiries-select-all">
                        <input type="checkbox" checked={allSelected} onChange={toggleAll} disabled={busy} />
                        Select all
                    </label>
                    <button
                        type="button"
                        className="caption underline disabled:opacity-40 disabled:no-underline"
                        style={{ color: 'var(--ink-2)' }}
                        disabled={busy || selected.size === 0}
                        onClick={() => removeIds(
                            Array.from(selected),
                            `Remove ${selected.size} enquir${selected.size === 1 ? 'y' : 'ies'} from the admin view? The original record${selected.size === 1 ? '' : 's'} will remain in the database.`,
                        )}
                        data-testid="enquiries-remove-selected"
                    >
                        Remove selected{selected.size ? ` (${selected.size})` : ''}
                    </button>
                </div>
            )}

            <div className="mt-4 flex flex-col gap-0">
                {items.map((e) => (
                    <div key={e.id} className="rule-bottom py-8 grid grid-cols-12 gap-6" data-testid={`enquiry-row-${e.id}`}>
                        <div className="col-span-12 md:col-span-3">
                            <label className="flex items-start gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    className="mt-1"
                                    checked={selected.has(e.id)}
                                    onChange={() => toggle(e.id)}
                                    disabled={busy}
                                    data-testid={`enquiry-select-${e.id}`}
                                />
                                <span>
                                    <div className="label" style={{ color: 'var(--clay)' }}>{e.type}</div>
                                    <div className="serif text-xl mt-2">{e.name}</div>
                                    <div className="caption">{e.email}{e.phone ? ` · ${e.phone}` : ''}</div>
                                    <div className="caption mt-1" style={{ fontSize: 12, color: 'var(--ink-3)' }}>{new Date(e.created_at).toLocaleString('en-AU')}</div>
                                </span>
                            </label>
                        </div>
                        <div className="col-span-12 md:col-span-9">
                            {e.artwork_title && <div className="label mb-2" style={{ color: 'var(--ink-2)' }}>Re: {e.artwork_title}</div>}
                            <p className="literary">{e.message}</p>
                            <button
                                type="button"
                                className="caption underline mt-4 disabled:opacity-40"
                                style={{ color: 'var(--ink-3)' }}
                                disabled={busy}
                                onClick={() => removeIds(
                                    [e.id],
                                    'Remove this enquiry from the admin view? The original record will remain in the database.',
                                )}
                                data-testid={`enquiry-remove-${e.id}`}
                            >
                                Remove
                            </button>
                        </div>
                    </div>
                ))}
                {items.length === 0 && <p className="literary">No enquiries yet.</p>}
            </div>
        </div>
    );
}

export function AdminNewsletter() {
    const [items, setItems] = useState([]);
    const [busy, setBusy] = useState(false);
    const { selected, toggle, toggleAll, clear, allSelected } = useSelection(items);
    const reload = () => adminNewsletter().then(setItems);
    useEffect(() => { reload(); }, []);

    const removeIds = async (ids, msg) => {
        if (!ids.length) return;
        if (!window.confirm(msg)) return;
        setBusy(true);
        try {
            await hideNewsletter(ids);
            await reload();
            clear();
        } finally { setBusy(false); }
    };

    return (
        <div data-testid="admin-newsletter" className="py-10">
            <span className="section-no">§ The Circle</span>
            <h1 className="serif text-5xl md:text-7xl mt-3 leading-[0.95]">Newsletter</h1>
            <p className="literary mt-4">{items.length} subscribers</p>

            {items.length > 0 && (
                <div className="mt-8 flex items-center gap-6 rule-bottom pb-4">
                    <label className="flex items-center gap-2 caption cursor-pointer" data-testid="newsletter-select-all">
                        <input type="checkbox" checked={allSelected} onChange={toggleAll} disabled={busy} />
                        Select all
                    </label>
                    <button
                        type="button"
                        className="caption underline disabled:opacity-40 disabled:no-underline"
                        style={{ color: 'var(--ink-2)' }}
                        disabled={busy || selected.size === 0}
                        onClick={() => removeIds(
                            Array.from(selected),
                            `Remove ${selected.size} sign-up${selected.size === 1 ? '' : 's'} from the admin view? The original record${selected.size === 1 ? '' : 's'} will remain in the database.`,
                        )}
                        data-testid="newsletter-remove-selected"
                    >
                        Remove selected{selected.size ? ` (${selected.size})` : ''}
                    </button>
                </div>
            )}

            <div className="mt-4 flex flex-col">
                {items.map((s) => (
                    <div key={s.id} className="rule-bottom py-4 flex items-center justify-between gap-4" data-testid={`subscriber-row-${s.id}`}>
                        <label className="flex items-center gap-3 cursor-pointer flex-1 min-w-0">
                            <input
                                type="checkbox"
                                checked={selected.has(s.id)}
                                onChange={() => toggle(s.id)}
                                disabled={busy}
                                data-testid={`subscriber-select-${s.id}`}
                            />
                            <span className="serif text-lg truncate">{s.email}</span>
                        </label>
                        <span className="caption">{new Date(s.created_at).toLocaleDateString('en-AU')}</span>
                        <button
                            type="button"
                            className="caption underline disabled:opacity-40"
                            style={{ color: 'var(--ink-3)' }}
                            disabled={busy}
                            onClick={() => removeIds(
                                [s.id],
                                'Remove this sign-up from the admin view? The original record will remain in the database.',
                            )}
                            data-testid={`subscriber-remove-${s.id}`}
                        >
                            Remove
                        </button>
                    </div>
                ))}
                {items.length === 0 && <p className="literary">No subscribers yet.</p>}
            </div>
        </div>
    );
}
